import { StandardThunk } from '../../../store';
import { PoiModalNearbyPoiType, PoiModalSelectedFlightlogEventType } from '../../../types/poiModal';
import {
  PointOfInterest,
  CustomPointOfInterest,
  PoiCodePreference
} from '../../../common/api/spidertracks-sdk/types/PointOfInterest';
import { getInstance } from '../../../common/api/spidertracks-sdk';
import {
  setSelectedFlightlogEvent,
  setNearbyPois,
  setLoadingNearbyPois,
  setAllLocationTypes,
  setDraftForm
} from './poiModal';
import { findFlightlogsEventPoiByEventId } from '../../../components/History/pois/utils/findFlightlogsEventPoiByEventId';
import { getUserOrgData } from '../../selectors/userData';
import { showErrorNotification } from '../../../helpers/globalNotifications';
const mockLatitude = -41.2865;
const mockLongitude = 174.7762;

const getPoiCodeWithPreference = (
  orgLocationCodePreference: PoiCodePreference | undefined,
  poi: PointOfInterest
) => {
  const isCustomPoi = (poi: PointOfInterest): poi is CustomPointOfInterest => 'code' in poi;
  if (isCustomPoi(poi) && poi.code) {
    return poi.code;
  }

  if (!isCustomPoi(poi) && poi.other) {
    return poi.other;
  }
  if (orgLocationCodePreference && !isCustomPoi(poi) && poi[orgLocationCodePreference]) {
    return poi[orgLocationCodePreference];
  }

  return 'no code';
};

export const loadNearbyPoiInfo = (
  // ToDo: rework on this once we have the real flightlogs data saved to redux from history page
  rowData: any,
  selectedEventId: string
): StandardThunk<void> => async (dispatch, getState) => {
  if (!rowData || !selectedEventId) return;

  dispatch(setLoadingNearbyPois(true));

  try {
    const transformedRowData: PoiModalSelectedFlightlogEventType = {
      track: {
        id: rowData.id,
        flightId: rowData.key,
        trackId: rowData.flightId,
        imei: rowData.imei,
        flightTime: rowData.flightTime,
        orgId: rowData.orgId,
        aircraft: {
          id: rowData.aircraftId,
          vehicleType: rowData.aircraftType
        },
        flightLog: rowData.flightLog ?? []
      },
      selectedEventId: selectedEventId
    };
    dispatch(setSelectedFlightlogEvent(transformedRowData));

    const eventLocationData = rowData.flightLog
      ? findFlightlogsEventPoiByEventId(selectedEventId, rowData.flightLog)
      : null;

    // ToDo: get rid of the mock data once we have real lat long from the event
    // const latitude = eventLocationData?.latitude;
    // const longitude = eventLocationData?.longitude;
    const latitude = mockLatitude;
    const longitude = mockLongitude;

    if (!latitude || !longitude) {
      console.error(
        `Unable to find coordinates for event ${selectedEventId}. Location data is missing or incomplete.`
      );
      showErrorNotification('Failed to load coordinates for this event -- please try again later');
      return;
    }
    dispatch(setDraftForm({ latitude, longitude }));

    const SpidertracksSDK = getInstance();
    const locationTypes = await SpidertracksSDK.getPoiService().getLocationTypes();
    dispatch(setAllLocationTypes(locationTypes));
    const nearbyPois = await SpidertracksSDK.getPoiService().getPois(
      rowData.orgId,
      latitude,
      longitude
    );
    const state = getState();
    const userOrgData = getUserOrgData(state);
    const orgLocationCodePreference = userOrgData.find(org => org.org.id === rowData.orgId)?.org
      .poiCodePreference;

    const transformedNearbyPois: PoiModalNearbyPoiType[] = nearbyPois.map(poi => ({
      locationId: poi.locationId,
      distance: Number(poi.distance?.toFixed(2) ?? 0),
      code: getPoiCodeWithPreference(orgLocationCodePreference, poi),
      name: poi.name,
      latitude: Number(poi.latitude),
      longitude: Number(poi.longitude),
      locationTypeId: poi.locationTypeId
    }));

    const getNearbyPoiFromCurrentTrack = (): PoiModalNearbyPoiType | null => {
      if (!rowData.flightLog) return null;

      const flightLogEvents = rowData.flightLog;
      const locationData = findFlightlogsEventPoiByEventId(selectedEventId, flightLogEvents);
      if (!locationData) return null;

      return {
        locationId: locationData.locationId,
        distance: rowData.distance,
        code: getPoiCodeWithPreference(orgLocationCodePreference, locationData),
        name: locationData?.name ?? undefined,
        latitude: locationData?.latitude ?? undefined,
        longitude: locationData?.longitude ?? undefined,
        locationTypeId: locationData?.locationTypeId
      };
    };

    const nearbyPoiFromCurrentTrack = getNearbyPoiFromCurrentTrack();

    const combinedNearbyPois = nearbyPoiFromCurrentTrack
      ? [nearbyPoiFromCurrentTrack, ...transformedNearbyPois]
      : transformedNearbyPois;

    dispatch(setNearbyPois(combinedNearbyPois));
  } catch (error) {
    console.error('Failed to load nearby points of interest:', error);
    throw new Error('Failed to load nearby points of interest -- please try again later');
  } finally {
    dispatch(setLoadingNearbyPois(false));
  }
};
